
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Plus, Clipboard, ArrowLeft, AlertTriangle, X, Edit, CheckCircle2 } from 'lucide-react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

// Mock data for skills
const mockSkills = [
  {
    id: '1',
    name: 'React',
    category: 'frontend',
    proficiency: 'advanced',
  },
  {
    id: '2',
    name: 'TypeScript',
    category: 'language',
    proficiency: 'intermediate',
  },
  {
    id: '3',
    name: 'Node.js',
    category: 'backend',
    proficiency: 'beginner',
  },
  {
    id: '4',
    name: 'UI/UX Design',
    category: 'design',
    proficiency: 'intermediate',
  },
];

// Define form schema for adding/editing skills
const skillFormSchema = z.object({
  name: z.string().min(2, {
    message: "Skill name must be at least 2 characters.",
  }),
  category: z.string().min(2, {
    message: "Category must be at least 2 characters.",
  }),
  proficiency: z.enum(["beginner", "intermediate", "advanced"], {
    message: "Please select a valid proficiency level.",
  }),
});

export default function ProjectSkills() {
  const { user } = useAuth();
  const [projectName, setProjectName] = useState<string>('');
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<typeof mockSkills[0] | null>(null);
  
  // Get the selected project from localStorage
  React.useEffect(() => {
    const storedProjectName = localStorage.getItem('selectedProject');
    if (storedProjectName) {
      setProjectName(storedProjectName);
    }
  }, []);

  // Form for adding/editing skills
  const form = useForm<z.infer<typeof skillFormSchema>>({
    resolver: zodResolver(skillFormSchema),
    defaultValues: {
      name: "",
      category: "",
      proficiency: "intermediate",
    },
  });

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!isAddSkillOpen) {
      form.reset();
      setEditingSkill(null);
    }
  }, [isAddSkillOpen, form]);

  // Set form values when editing a skill
  React.useEffect(() => {
    if (editingSkill) {
      form.setValue("name", editingSkill.name);
      form.setValue("category", editingSkill.category);
      form.setValue("proficiency", editingSkill.proficiency as "beginner" | "intermediate" | "advanced");
      setIsAddSkillOpen(true);
    }
  }, [editingSkill, form]);

  // In a real application, we would fetch the skills for the current project
  // For now, use mock data
  const { data: skills, isLoading, error } = useQuery({
    queryKey: ['skills', 'project'],
    queryFn: async () => {
      // Simulating API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real application, we would fetch skills from the API
      // For now, return mock data
      return mockSkills;
    },
    enabled: !!user
  });

  const handleAddSkill = (data: z.infer<typeof skillFormSchema>) => {
    if (editingSkill) {
      // Update existing skill logic would go here
      toast({
        title: "Skill updated",
        description: `${data.name} has been updated.`,
      });
    } else {
      // Add new skill logic would go here
      toast({
        title: "Skill added",
        description: `${data.name} has been added to the project.`,
      });
    }
    setIsAddSkillOpen(false);
  };

  const handleEditSkill = (skill: typeof mockSkills[0]) => {
    setEditingSkill(skill);
  };

  const handleDeleteSkill = (skillId: string) => {
    // Delete skill logic would go here
    toast({
      title: "Skill removed",
      description: "Skill has been removed from the project.",
    });
  };

  const getProficiencyBadgeVariant = (proficiency: string) => {
    switch (proficiency) {
      case 'advanced':
        return 'default';
      case 'intermediate':
        return 'secondary';
      case 'beginner':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  return (
    <DashboardLayout>
      <motion.div
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center space-x-4 mb-6">
          <Button variant="outline" size="icon" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-semibold">
              {projectName || 'Project'} Skills
            </h1>
            <p className="text-muted-foreground">Manage project skills and competencies</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <motion.h2 
            className="text-xl font-medium flex items-center gap-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Clipboard className="h-5 w-5" />
            Skills Overview
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Dialog open={isAddSkillOpen} onOpenChange={setIsAddSkillOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Skill
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{editingSkill ? 'Edit Skill' : 'Add Skill'}</DialogTitle>
                  <DialogDescription>
                    {editingSkill 
                      ? 'Edit the skill details below. Click save when you\'re done.'
                      : 'Add a new skill to the project. Skills help track team capabilities.'}
                  </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleAddSkill)} className="space-y-4 py-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Skill Name</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. React, JavaScript, UI Design" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Frontend, Backend, Design" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="proficiency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Proficiency Level</FormLabel>
                          <FormControl>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              {...field}
                            >
                              <option value="beginner">Beginner</option>
                              <option value="intermediate">Intermediate</option>
                              <option value="advanced">Advanced</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <DialogFooter className="pt-4">
                      <Button type="submit">{editingSkill ? 'Save Changes' : 'Add Skill'}</Button>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </motion.div>
        </div>
        
        {/* Skills Table */}
        <motion.div 
          className="w-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {isLoading ? (
            // Skeleton loading
            <Card>
              <CardContent className="p-6">
                <div className="space-y-2">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              </CardContent>
            </Card>
          ) : error ? (
            // Error state
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
                <h3 className="text-lg font-medium">Error loading skills</h3>
                <p className="text-muted-foreground">Please try again later.</p>
              </CardContent>
            </Card>
          ) : skills && skills.length > 0 ? (
            // Display skills in a table
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Skill</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Proficiency</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {skills.map((skill) => (
                      <TableRow key={skill.id}>
                        <TableCell className="font-medium">{skill.name}</TableCell>
                        <TableCell className="capitalize">{skill.category}</TableCell>
                        <TableCell>
                          <Badge variant={getProficiencyBadgeVariant(skill.proficiency)} className="capitalize">
                            {skill.proficiency}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleEditSkill(skill)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteSkill(skill.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            // Empty state
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                <Clipboard className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No skills found</h3>
                <p className="text-muted-foreground">Add skills to track team capabilities for this project.</p>
                <Button className="mt-4" onClick={() => setIsAddSkillOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add First Skill
                </Button>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </motion.div>
    </DashboardLayout>
  );
}
